import { useCallback } from "react";

import type { CardOnboardingPauseReason } from "@/modules/dashboard/organizations/types/organizationsAdmin.types";
import {
  cardProviderLabelKey,
  PAUSE_REASON_LABEL_KEYS,
} from "@/modules/dashboard/organizations/utils/organizationOverview";
import type { KycStatus } from "@/modules/verification/types/verification-api.types";
import { useTranslations } from "@/shared/lang/useTranslations";

import type { CardholderBoardItem, CardProviderCode } from "../types/cardholderOnboarding.types";
import { cardholderHeadlineKey } from "../utils/cardholderBoard";

/**
 * The sentences several surfaces of the board share. The provider is always named by its Coloca label, and
 * the pause reasons are the organization panel's own wording, so both screens say the same thing.
 */
export function useCardholderCopy() {
  const { t } = useTranslations("cardholderOnboarding");
  const { t: tOrg } = useTranslations("organizations");

  const providerLabel = useCallback(
    (provider: CardProviderCode | null | undefined) => {
      const key = cardProviderLabelKey(provider ?? null);
      return key ? tOrg(key) : t("provider_unknown");
    },
    [t, tOrg],
  );

  const pauseReason = useCallback(
    (reason: CardOnboardingPauseReason) => tOrg(PAUSE_REASON_LABEL_KEYS[reason]),
    [tOrg],
  );

  const kycStatus = useCallback((status: KycStatus) => t(`kyc_${status}`), [t]);

  const headline = useCallback(
    (item: CardholderBoardItem, provider: string) =>
      t(cardholderHeadlineKey(item), {
        provider,
        reason: item.pause_reason ? pauseReason(item.pause_reason) : "",
      }),
    [t, pauseReason],
  );

  return { providerLabel, pauseReason, kycStatus, headline };
}
